import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title, 
  description, 
  keywords,
  canonicalUrl,
  ogImage = 'https://bagi.rs/logo.png',
  ogType = 'website',
  breadcrumbs = [],
  article = null,
  product = null,
  faq = null,
  noindex = false
}) => {
  const siteUrl = 'https://bagi.rs';
  const fullTitle = title.includes('BAGI') ? title : `${title} | BAGI Trans`;
  const currentUrl = canonicalUrl || siteUrl;
  
  // Generate JSON-LD for breadcrumbs
  const breadcrumbSchema = breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((breadcrumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": breadcrumb.name,
      "item": `${siteUrl}${breadcrumb.url}`
    }))
  } : null;

  // Generate JSON-LD for FAQ
  const faqSchema = faq ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  // Generate JSON-LD for Article
  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": ogImage,
    "author": {
      "@type": "Organization",
      "name": "BAGI Trans"
    },
    "publisher": {
      "@type": "Organization",
      "name": "BAGI Trans",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      }
    },
    "datePublished": article.datePublished,
    "dateModified": article.dateModified || article.datePublished,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": currentUrl
    }
  } : null;

  // Generate JSON-LD for Product
  const productSchema = product ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": product.images || [ogImage],
    "brand": {
      "@type": "Brand",
      "name": "BAGI Trans"
    },
    "offers": {
      "@type": "Offer",
      "price": product.price,
      "priceCurrency": "RSD",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "BAGI Trans"
      }
    },
    "category": product.category || "Građevinska mehanizacija"
  } : null;
  
  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"} />
      <meta name="author" content="BAGI Trans" />
      <meta httpEquiv="content-language" content="sr" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="BAGI Trans" />
      <meta property="og:locale" content="sr_RS" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Additional SEO tags for 2025 */}
      <meta name="format-detection" content="telephone=yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="BAGI Trans" />
      
      {/* Preload critical resources */}
      <link rel="preload" href="/logo.png" as="image" type="image/png" />
      
      {/* Structured Data */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
      
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}

      {/* Schema.org markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "BAGI Trans",
          "alternateName": "BAGI",
          "url": siteUrl,
          "logo": `${siteUrl}/logo.png`,
          "description": "BAGI Trans - Vaš pouzdani partner za prodaju bagera i građevinske mehanizacije. Najveći izbor polovnih bagera, utovarivača i druge mehanizacije u Srbiji.",
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+381 69 800 31 28",
            "contactType": "customer service",
            "areaServed": "RS",
            "availableLanguage": ["Serbian"]
          },
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Nikole Tesle 165A",
            "addressLocality": "Kraljevo",
            "postalCode": "36000",
            "addressCountry": "RS"
          },
          "sameAs": [
            "https://www.facebook.com/bagitrans/",
            "https://www.instagram.com/bagi.trans/",
            "https://www.youtube.com/@dejanlazarevic6509"
          ]
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "url": siteUrl,
          "name": "BAGI Trans",
          "description": "Prodaja bagera i građevinske mehanizacije",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${siteUrl}/?s={search_term_string}`
            },
            "query-input": "required name=search_term_string"
          },
          "hasPart": [
            {
              "@type": "WebPage",
              "url": `${siteUrl}/`,
              "name": "Početna",
              "description": "Prodaja bagera i građevinske mehanizacije"
            },
            {
              "@type": "WebPage",
              "url": `${siteUrl}/bagis`,
              "name": "BAGIS",
              "description": "BAGIS - Informacioni sistem za upravljanje voznim parkom"
            },
            {
              "@type": "WebPage",
              "url": `${siteUrl}/o-nama`,
              "name": "O nama",
              "description": "Saznajte više o BAGI Trans kompaniji"
            },
            {
              "@type": "WebPage",
              "url": `${siteUrl}/sport`,
              "name": "Sport",
              "description": "Sportske aktivnosti i sponzorstva - Bagi Trans"
            },
            {
              "@type": "WebPage",
              "url": `${siteUrl}/blog`,
              "name": "Blog",
              "description": "Novosti i članci o građevinskoj mehanizaciji - Bagi Trans"
            },
            {
              "@type": "WebPage",
              "url": `${siteUrl}/kontakt`,
              "name": "Kontakt",
              "description": "Kontaktirajte BAGI Trans"
            }
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEO; 